import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ExternalDataService, NonScrapItemTransactionDto, Zone, Agent, BulkSettlementUpdateRequest } from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { PaginationComponent } from '../shared/pagination/pagination.component';

@Component({
  selector: 'app-non-scrap-transactions',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent],
  templateUrl: './non-scrap-transactions.component.html',
  styleUrl: './non-scrap-transactions.component.css'
})
export class NonScrapTransactionsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Data properties
  transactions: NonScrapItemTransactionDto[] = [];
  agents: Agent[] = [];
  zones: Zone[] = [];
  
  // Loading and error states
  loading = false;
  errorMessage = '';
  successMessage = '';
  
  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages: number = 1;
  
  // Filter properties
  selectedAgentId = '';
  selectedZoneId = '';
  selectedIsSettled: string = '';
  fromDate = '';
  toDate = '';
  
  // Expanded rows
  expandedRows: Set<string> = new Set();
  
  // Bulk settlement properties
  selectedTransactions: string[] = [];
  showBulkSettlementForm = false;
  bulkSettlementForm = {
    settlementDate: '',
    settlementMedium: '',
    settlementNotes: ''
  };
  bulkSettlementLoading = false;
  
  // Make Math available in template
  Math = Math;

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Set default date values (last 30 days)
    this.fromDate = this.getDefaultFromDate();
    this.toDate = this.getDefaultToDate();
    
    this.loadAgents();
    this.loadZones();
    this.loadTransactions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAgents(): void {
    this.externalDataService.getAllAgents(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.agents = agents;
        },
        error: (error) => {
          console.error('Error loading agents:', error);
        }
      });
  }

  loadZones(): void {
    this.externalDataService.getAllZones()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.zones = zones;
        },
        error: (error) => {
          console.error('Error loading zones:', error);
        }
      });
  }

  loadTransactions(): void {
    this.loading = true;
    this.errorMessage = '';
    
    const pageIndex = this.currentPage - 1;
    const agentId = this.selectedAgentId || undefined;
    const geoZoneId = this.selectedZoneId || undefined;
    const isSettled = this.selectedIsSettled === 'true' ? true : 
                      this.selectedIsSettled === 'false' ? false : 
                      undefined;
    const fromDate = this.fromDate ? new Date(this.fromDate).toISOString() : undefined;
    const toDate = this.toDate ? (() => {
      const date = new Date(this.toDate);
      date.setHours(23, 59, 59, 999);
      return date.toISOString();
    })() : undefined;
    
    this.externalDataService.getNonScrapItemTransactions(
      pageIndex,
      this.pageSize,
      agentId,
      geoZoneId,
      isSettled,
      fromDate,
      toDate
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.transactions = data.items;
          this.totalCount = data.totalCount;
          this.calculateTotalPages();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading non-scrap transactions:', error);
          
          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to view non-scrap transactions.';
          } else if (error.status === 404) {
            this.errorMessage = 'Non-scrap transactions not found.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to load non-scrap transactions. Please try again.';
          }
          
          this.loading = false;
        }
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadTransactions();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.loadTransactions();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadTransactions();
  }

  clearFilters(): void {
    this.selectedAgentId = '';
    this.selectedZoneId = '';
    this.selectedIsSettled = '';
    this.fromDate = this.getDefaultFromDate();
    this.toDate = this.getDefaultToDate();
    this.currentPage = 1;
    this.loadTransactions();
  }

  private calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  private getDefaultFromDate(): string {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  }

  private getDefaultToDate(): string {
    const date = new Date();
    return date.toISOString().split('T')[0];
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  formatNumber(value: number, decimals: number = 2): string {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  }

  // Expandable row methods
  toggleRow(transactionId: string): void {
    if (this.expandedRows.has(transactionId)) {
      this.expandedRows.delete(transactionId);
    } else {
      this.expandedRows.add(transactionId);
    }
  }

  isRowExpanded(transactionId: string): boolean {
    return this.expandedRows.has(transactionId);
  }

  // Bulk settlement methods
  toggleTransactionSelection(transactionId: string): void {
    const transaction = this.transactions.find(t => t.id === transactionId);
    // Don't allow selection of settled transactions
    if (transaction?.isSettled) {
      return;
    }
    
    const index = this.selectedTransactions.indexOf(transactionId);
    if (index > -1) {
      this.selectedTransactions.splice(index, 1);
    } else {
      this.selectedTransactions.push(transactionId);
    }
  }

  isTransactionSelected(transactionId: string): boolean {
    return this.selectedTransactions.includes(transactionId);
  }

  canSelectTransaction(transactionId: string): boolean {
    const transaction = this.transactions.find(t => t.id === transactionId);
    return transaction ? !transaction.isSettled : false;
  }

  selectAllTransactions(): void {
    // Only select transactions that are not settled
    this.selectedTransactions = this.transactions
      .filter(t => !t.isSettled)
      .map(t => t.id);
  }

  get selectableTransactionsCount(): number {
    return this.transactions.filter(t => !t.isSettled).length;
  }

  get allSelectableTransactionsSelected(): boolean {
    const selectableCount = this.selectableTransactionsCount;
    return selectableCount > 0 && this.selectedTransactionsCount === selectableCount;
  }

  get someSelectableTransactionsSelected(): boolean {
    return this.selectedTransactionsCount > 0 && this.selectedTransactionsCount < this.selectableTransactionsCount;
  }

  clearSelection(): void {
    this.selectedTransactions = [];
    this.showBulkSettlementForm = false;
    this.resetBulkSettlementForm();
  }

  openBulkSettlementForm(): void {
    if (this.selectedTransactions.length === 0) {
      this.errorMessage = 'Please select at least one transaction for bulk settlement update.';
      return;
    }
    
    // Set default date to today
    this.bulkSettlementForm.settlementDate = new Date().toISOString().split('T')[0];
    this.showBulkSettlementForm = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  closeBulkSettlementForm(): void {
    this.showBulkSettlementForm = false;
    this.resetBulkSettlementForm();
  }

  resetBulkSettlementForm(): void {
    this.bulkSettlementForm = {
      settlementDate: '',
      settlementMedium: '',
      settlementNotes: ''
    };
  }

  submitBulkSettlement(): void {
    if (!this.validateBulkSettlementForm()) {
      return;
    }

    this.bulkSettlementLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const request: BulkSettlementUpdateRequest = {
      nonScrapItemTransactionIds: this.selectedTransactions,
      settlementDate: new Date(this.bulkSettlementForm.settlementDate).toISOString(),
      settlementMedium: this.bulkSettlementForm.settlementMedium,
      settlementNotes: this.bulkSettlementForm.settlementNotes
    };

    this.externalDataService.updateBulkSettlement(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.successMessage = `Successfully updated settlement for ${this.selectedTransactions.length} transaction(s).`;
          this.bulkSettlementLoading = false;
          this.closeBulkSettlementForm();
          this.clearSelection();
          this.loadTransactions(); // Refresh the data
        },
        error: (error) => {
          console.error('Error updating bulk settlement:', error);
          
          if (error.status === 401) {
            this.errorMessage = 'Authentication required. Please log in again.';
          } else if (error.status === 403) {
            this.errorMessage = 'Access denied. You do not have permission to update settlement.';
          } else if (error.status === 404) {
            this.errorMessage = 'One or more transactions not found.';
          } else if (error.status >= 500) {
            this.errorMessage = 'Server error. Please try again later.';
          } else if (error.status === 0 || error.status === undefined) {
            this.errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            this.errorMessage = 'Failed to update settlement. Please try again.';
          }
          
          this.bulkSettlementLoading = false;
        }
      });
  }

  private validateBulkSettlementForm(): boolean {
    if (!this.bulkSettlementForm.settlementDate) {
      this.errorMessage = 'Settlement date is required.';
      return false;
    }

    if (!this.bulkSettlementForm.settlementMedium?.trim()) {
      this.errorMessage = 'Settlement medium is required.';
      return false;
    }

    return true;
  }

  get selectedTransactionsCount(): number {
    return this.selectedTransactions.length;
  }

  get selectedTransactionsTotalAmount(): number {
    return this.transactions
      .filter(t => this.selectedTransactions.includes(t.id))
      .reduce((sum, t) => sum + (t.totalNonScrapPrice || 0), 0);
  }

  // Totals for current page items
  get totalPageAmount(): number {
    return this.transactions.reduce((sum, t) => sum + (t.totalNonScrapPrice || 0), 0);
  }
}
